// components/VariantGenerator.tsx
import React from "react";
import { toast } from "react-toastify";
import { Helper } from "@utils/index";
import theme from "@utils/theme";

interface VariantGeneratorProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  selectedColors: any[];
  selectedAttributes: any[];
  variantError: string;
  setVariantError: (error: string) => void;
  productData: any;
}

export const VariantGenerator: React.FC<VariantGeneratorProps> = ({
  values,
  setFieldValue,
  selectedColors,
  selectedAttributes,
  variantError,
  setVariantError,
  productData,
}) => {
  const cartesianProduct = (arrays: Array<any[]>) => {
    if (!arrays || arrays.length === 0) return [{}];
    return arrays.reduce(
      (acc, curr) => {
        const res: any[] = [];
        acc.forEach((a) => {
          curr.forEach((c) => {
            res.push({ ...a, ...c });
          });
        });
        return res;
      },
      [{}]
    );
  };

  const createVariantSignature = (variant: any) => {
    const sortedAttributes = variant.attributes
      ? Object.keys(variant.attributes)
          .sort()
          .map((key) => `${key}:${variant.attributes[key]}`)
          .join("|")
      : "";

    return `${variant.name}|${variant.code}|${sortedAttributes}`;
  };

  const variantExists = (newVariant: any) => {
    const newSignature = createVariantSignature(newVariant);
    return values.variants.some(
      (existingVariant: any) =>
        createVariantSignature(existingVariant) === newSignature
    );
  };

  const generateVariants = () => {
    setVariantError("");
    // If no colors and no attributes, warn user
    if (
      (!selectedColors || selectedColors.length === 0) &&
      (selectedAttributes || []).length === 0
    ) {
      setVariantError(
        "Please select at least one color or attribute to generate variants."
      );
      setFieldValue("variants", []);
      return;
    }

    // Use only user-selected attribute values
    const attrArrays = (selectedAttributes || [])
      .filter(
        (attr) =>
          Array.isArray(attr.selectedValues) && attr.selectedValues.length > 0
      )
      .map((attr) => attr.selectedValues.map((v: any) => ({ [attr.name]: v })));

    // If user has added attributes but selected none of their values => warn
    if (selectedAttributes.length > 0 && attrArrays.length === 0) {
      setVariantError(
        "Please select at least one value for selected attributes or remove the attribute(s)."
      );
      setFieldValue("variants", []);
      return;
    }

    const combos = attrArrays.length > 0 ? cartesianProduct(attrArrays) : [{}];
    const newVariants: any[] = [];
    const duplicateVariants: string[] = [];

    const colorsToUse =
      selectedColors && selectedColors.length > 0
        ? selectedColors
        : [{ id: "", name: "", code: "" }];

    for (const color of colorsToUse) {
      for (const combo of combos) {
        const newVariant = {
          colorId: color.id,
          name: color.name,
          code: color.code,
          attributes: combo,
          price: "",
          stock: "",
          sku: "",
          image: null,
        };

        // Check if this variant combination already exists
        if (variantExists(newVariant)) {
          const variantName = `${color.name ? color.name : ""}${
            Object.values(combo).length > 0
              ? `${color.name ? "-" : ""}${Object.values(combo).join("-")}`
              : ""
          }`;
          duplicateVariants.push(variantName);
        } else {
          newVariants.push(newVariant);
        }
      }
    }

    // Show error for duplicates
    if (duplicateVariants.length > 0) {
      toast.error(`Duplicate variants are skipped automatically`);
    }

    // Add new variants to existing ones
    if (newVariants.length > 0) {
      const allVariants = [...values.variants, ...newVariants];
      setFieldValue("variants", allVariants);
      toast.success(`Added ${newVariants.length} new variant(s)`);
    } else if (duplicateVariants.length === 0) {
      setVariantError(
        "No new variants to add. Please select colors and attributes."
      );
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        onClick={generateVariants}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "none",
          background: theme.colors.primary,
          color: "#fff",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        {!!productData?.id ? "Add More Variants" : "Generate Variants"}
      </button>
      <small style={{ marginLeft: 12, color: "#666" }}>
        (Select attribute values before generating)
      </small>
      {variantError && (
        <div style={{ color: "#d04444", marginTop: 8 }}>{variantError}</div>
      )}
    </div>
  );
};
